import { useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  Card,
  CardBody,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { Alert, message } from "antd";
import { useClubRegistrationState } from "src/hooks/useClubsHooks";

export default function ClubRegisterModal() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { t } = useTranslation();
  const [, contextHolder] = message.useMessage();
  
  const {
    clubNameEnReg,
    clubNameArReg,
    personNameEnReg,
    personNameArReg,
    mobileReg,
    emailReg,
    usernameReg,
    registrationStatus,
    registrationMessage,
    registrationError,
    registrationHasAlert,
    setClubNameEnReg,
    setClubNameArReg,
    setPersonNameEnReg,
    setPersonNameArReg,
    setMobileReg,
    setEmailReg,
    setUsernameReg,
    setRegistrationError,
    setRegistrationHasAlert,
    setRegistrationStatus,
    setRegistrationMessage,
    resetRegistrationForm,
    registerClub,
  } = useClubRegistrationState();

  useEffect(() => {
    if (registrationStatus === 'success') {
      const timeoutId = setTimeout(() => {
        resetRegistrationForm();
        onOpenChange();
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    } else if (registrationStatus === 'error') {
      setRegistrationError(registrationMessage);
    }
  }, [registrationStatus, registrationMessage, onOpenChange, resetRegistrationForm, setRegistrationError]);

  const validateForm = () => {
    if (!clubNameEnReg || !clubNameArReg || !personNameEnReg || !personNameArReg || !mobileReg || !emailReg || !usernameReg) {
      setRegistrationError(document.documentElement.dir === "rtl" ? "الرجاء إدخال جميع الحقول المطلوبة" : "Please fill in all required fields");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailReg)) {
      setRegistrationError(document.documentElement.dir === "rtl" ? "البريد الإلكتروني غير صحيح" : "Invalid email format");
      return false;
    }

    if (mobileReg.length < 8) {
      setRegistrationError(document.documentElement.dir === "rtl" ? "رقم الهاتف غير صحيح" : "Invalid mobile number");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setRegistrationError('');

    if (!validateForm()) {
      return;
    }

    try {
      await registerClub({
        club_name_en: clubNameEnReg,
        club_name_ar: clubNameArReg,
        person_name_en: personNameEnReg,
        person_name_ar: personNameArReg,
        mobile: mobileReg,
        email: emailReg,
        username: usernameReg,
      });
    } catch (error: any) {
      setRegistrationError(error.message || 'Failed to register club');
    }
  };

  const resetForm = () => {
    resetRegistrationForm();
  };

  return (
    <>
      {contextHolder}
      <Button onPress={onOpen} color="primary">
        {t('club.register')}
      </Button>
      <Modal 
        isOpen={isOpen} 
        onOpenChange={onOpenChange}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {t('club.register.title')}
              </ModalHeader>
              <ModalBody>
                {registrationHasAlert && (
                  <Alert
                    message={registrationError || registrationMessage}
                    type={registrationError || registrationStatus === 'error' ? "error" : "success"}
                    showIcon
                    closable
                    onClose={() => {
                      setRegistrationError('');
                      resetRegistrationForm();
                    }}
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Card>
                  <CardBody className="gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        autoFocus
                        label={t('club.name.en')}
                        placeholder={t('club.name.en.placeholder')}
                        variant="bordered"
                        value={clubNameEnReg}
                        onChange={(e) => setClubNameEnReg(e.target.value)}
                        isRequired
                      />
                      <Input
                        label={t('club.name.ar')}
                        placeholder={t('club.name.ar.placeholder')}
                        variant="bordered"
                        value={clubNameArReg}
                        onChange={(e) => setClubNameArReg(e.target.value)}
                        isRequired
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label={t('club.person.name.en')}
                        placeholder={t('club.person.name.en.placeholder')}
                        variant="bordered"
                        value={personNameEnReg}
                        onChange={(e) => setPersonNameEnReg(e.target.value)}
                        isRequired
                      />
                      <Input
                        label={t('club.person.name.ar')}
                        placeholder={t('club.person.name.ar.placeholder')}
                        variant="bordered"
                        value={personNameArReg}
                        onChange={(e) => setPersonNameArReg(e.target.value)}
                        isRequired
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label={t('club.mobile')}
                        placeholder={t('club.mobile.placeholder')}
                        variant="bordered"
                        value={mobileReg}
                        onChange={(e) => setMobileReg(e.target.value)}
                        isRequired
                      />
                      <Input
                        label={t('club.email')}
                        placeholder={t('club.email.placeholder')}
                        type="email"
                        variant="bordered"
                        value={emailReg}
                        onChange={(e) => setEmailReg(e.target.value)}
                        isRequired
                      />
                    </div>

                    <Input
                      label={t('club.username')}
                      placeholder={t('club.username.placeholder')}
                      variant="bordered"
                      value={usernameReg}
                      onChange={(e) => setUsernameReg(e.target.value)}
                      isRequired
                    />
                  </CardBody>
                </Card>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={() => {
                  resetForm();
                  onClose();
                }}>
                  {t('cancel')}
                </Button>
                <Button 
                  color="primary" 
                  onPress={handleSubmit}
                >
                  {t('register')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
